import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../services/api';

export interface PostProcessingMetric {
  label: string;
  value: number;
}

export interface KeyValuePoint {
  key: string;
  value: number;
}

export interface TagMetric {
  id: string;
  type: string | null;
  value: { key: string; value: number };
}

export interface CommunicationPattern {
  data: Record<string, string[]>;
  groups: string[];
  matrix: number[][];
}

export interface PostProcessingMetrics {
  extensionByCount: PostProcessingMetric[];
  extensionBySize: PostProcessingMetric[];
  typeByCount: PostProcessingMetric[];
  typeBySize: PostProcessingMetric[];
  documentCountByDate: KeyValuePoint[];
  startDate: number;
  endDate: number;
  emailDomains: KeyValuePoint[];
  systemTags: TagMetric[];
  namedEntities: TagMetric[];
  communicationPatterns: CommunicationPattern[];
}

/** Post-processing metrics for the whole project, or one collection when collectionId is given. */
export function usePostProcessingMetrics(projectId: string, collectionId?: string) {
  const base = collectionId
    ? `/project/${projectId}/collection/${collectionId}`
    : `/project/${projectId}`;
  return useQuery<PostProcessingMetrics>({
    queryKey: ['postProcessingMetrics', projectId, collectionId ?? null],
    queryFn: () => api.get<PostProcessingMetrics>(`${base}/postprocessingmetrics`),
    enabled: !!projectId,
  });
}

// Named Entities chart: the aggregate metrics endpoint only aggregates the
// `person` entity type, so every other type needs its own call (Angular parity:
// PostProcessingMetricsService.GetNamedEntities). `type` is the backend
// ResourceEntityType value (EntityType.id) — an unknown value 500s in
// fromValue(), and the PII values contain spaces, hence the encode.
export function useNamedEntities(projectId: string, type: string, enabled: boolean, collectionId?: string) {
  const scope = collectionId ? `/project/${projectId}/collection/${collectionId}` : `/project/${projectId}`;

  return useQuery<TagMetric[]>({
    queryKey: ['namedEntities', projectId, collectionId ?? null, type],
    queryFn: () => api.get<TagMetric[]>(`${scope}/postprocessingmetrics/entity/${encodeURIComponent(type)}`),
    enabled: !!projectId && enabled,
  });
}

// Communication Chart slider: refetch the chord patterns for a given number of
// top communicators. `count` mirrors the Angular slider (1-100, default 50).
// Disabled until the user moves the slider — the aggregate metrics endpoint
// already supplies the default patterns for the initial render.
export function useCommunicationPatterns(projectId: string, count: number, enabled: boolean) {
  return useQuery<CommunicationPattern[]>({
    queryKey: ['communicationPatterns', projectId, count],
    queryFn: () =>
      api.get<CommunicationPattern[]>(`/project/${projectId}/postprocessingmetrics/communication?count=${count}`),
    enabled: !!projectId && enabled,
    placeholderData: keepPreviousData,
  });
}
